// Resolves a tenant_user's effective per-tab access, purely from their own
// explicit tenant_user_module_permissions rows (one row per AdminDashboard
// tab they can see, each with its own view/edit flags) -- plus which
// specific driver(s) and vehicle(s) their data is scoped to.
//
// Safety-first design: a tenant_user with NO active
// tenant_user_module_permissions rows is "unrestricted" -- the existing,
// unchanged behaviour every current admin/driver already has. Only
// explicitly giving someone at least one tab (via the Team Access screen
// in Settings) switches them to the restricted, tab-by-tab view -- and
// from then on they see ONLY the tabs they were explicitly given, nothing
// else, deny-by-default. This makes the whole permission system purely
// additive/opt-in per person; nobody currently using the app is affected
// until you deliberately configure them.
//
// permission_groups/permission_group_rules still exist as named presets
// (e.g. "Leaseholder") -- the Team Access screen can copy one in as a
// starting point, but they're a write-time convenience, not something
// read live here. Every restricted person's real access is always their
// own tenant_user_module_permissions rows, so what you see in Team Access
// is exactly what's enforced, with no group indirection to reason through.
import { supabase } from "@/lib/supabase";

/** Every AdminDashboard tab a person can be individually granted access
 *  to. Kept in sync with mainTabs in AdminDashboard.tsx by hand -- there's
 *  no single shared source today, so a new admin tab needs adding here too
 *  if it should be selectable in Team Access. */
export const ALL_MODULE_KEYS = [
  "closedcheck",
  "matchhub",
  "summary",
  "timesheets",
  "drivers",
  "fleet",
  "fleetpulse",
  "fleetcapacity",
  "roster",
  "jobinput",
  "reports",
  "crm",
  "exportbuilder",
  "ecard",
  "payout",
  "insurance",
  "ivrcalls",
  "commsbook",
  "settings",
] as const;

export type ModuleKey = (typeof ALL_MODULE_KEYS)[number];

export interface EffectiveModuleAccess {
  can_view: boolean;
  can_edit: boolean;
}

export interface EffectivePermissions {
  isUnrestricted: boolean;
  byModule: Partial<Record<ModuleKey, EffectiveModuleAccess>>;
  /** driver_name values this tenant_user is linked to via tenant_user_driver_access. */
  linkedDriverNames: string[];
  /** vehicle_number values this tenant_user is linked to via tenant_user_vehicle_access. */
  linkedVehicleNumbers: string[];
}

export const UNRESTRICTED_PERMISSIONS: EffectivePermissions = {
  isUnrestricted: true,
  byModule: {},
  linkedDriverNames: [],
  linkedVehicleNumbers: [],
};

interface ModulePermissionRow {
  module_key: string;
  can_view: boolean;
  can_edit: boolean;
}

export async function loadEffectivePermissions(tenantUserId: string | null): Promise<EffectivePermissions> {
  if (!tenantUserId) return UNRESTRICTED_PERMISSIONS;

  const [moduleRes, driverRes, vehicleRes] = await Promise.all([
    supabase.from("tenant_user_module_permissions").select("module_key, can_view, can_edit").eq("tenant_user_id", tenantUserId).eq("is_active", true),
    supabase.from("tenant_user_driver_access").select("driver_name").eq("tenant_user_id", tenantUserId).eq("is_active", true),
    supabase.from("tenant_user_vehicle_access").select("vehicle_number").eq("tenant_user_id", tenantUserId).eq("is_active", true),
  ]);

  if (moduleRes.error) {
    console.error("Error loading tenant_user_module_permissions:", moduleRes.error);
    return UNRESTRICTED_PERMISSIONS;
  }

  const moduleRows = (moduleRes.data as ModulePermissionRow[] | null) || [];
  // No explicit tab grants at all -- fully unrestricted, matching every
  // current user's existing behaviour.
  if (moduleRows.length === 0) return UNRESTRICTED_PERMISSIONS;

  const byModule: Partial<Record<ModuleKey, EffectiveModuleAccess>> = {};
  for (const row of moduleRows) {
    byModule[row.module_key as ModuleKey] = { can_view: row.can_view, can_edit: row.can_edit };
  }

  return {
    isUnrestricted: false,
    byModule,
    linkedDriverNames: ((driverRes.data as { driver_name: string }[] | null) || []).map((r) => r.driver_name),
    linkedVehicleNumbers: ((vehicleRes.data as { vehicle_number: string }[] | null) || []).map((r) => r.vehicle_number),
  };
}

/** Whether a restricted user can see a given AdminDashboard tab at all. */
export function canViewTab(permissions: EffectivePermissions, tab: string): boolean {
  if (permissions.isUnrestricted) return true;
  return !!permissions.byModule[tab as ModuleKey]?.can_view;
}

/** Whether a restricted user can edit within a given AdminDashboard tab
 *  (vs. read-only). Always true when unrestricted. */
export function canEditTab(permissions: EffectivePermissions, tab: string): boolean {
  if (permissions.isUnrestricted) return true;
  return !!permissions.byModule[tab as ModuleKey]?.can_edit;
}
